import { useEffect, useRef, useState } from "react";
import { X, Upload, Loader2, ImageOff } from "lucide-react";
import { toast } from "sonner";
import { adminApi, fmtErr } from "./api";

export const StatusChip = ({ status }) => {
  const map = {
    published: "bg-emerald-100 text-emerald-800 border-emerald-200",
    draft: "bg-amber-100 text-amber-800 border-amber-200",
    unpublished: "bg-slate-200 text-slate-600 border-slate-300",
  };
  return (
    <span className={`inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest border rounded-sm ${map[status] || map.draft}`} data-testid={`status-chip-${status}`}>
      {status}
    </span>
  );
};

export const RoleBadge = ({ role }) => {
  const map = { super_admin: "bg-[#0A1128] text-white", director: "bg-[#C5221F] text-white", hr: "bg-slate-600 text-white" };
  const label = { super_admin: "Super Admin", director: "Director", hr: "HR" }[role] || role;
  return <span className={`inline-block px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest rounded-sm ${map[role] || "bg-slate-200"}`}>{label}</span>;
};

export const Field = ({ label, hint, children }) => (
  <div>
    <label className="block text-[11px] font-bold uppercase tracking-widest text-slate-500 mb-1.5">{label}</label>
    {children}
    {hint && <p className="mt-1 text-xs text-slate-400">{hint}</p>}
  </div>
);

export const inputCls =
  "w-full border border-slate-300 bg-white px-3.5 py-2.5 text-sm text-slate-900 placeholder:text-slate-400 focus:outline-none focus:border-[#C5221F] focus:ring-1 focus:ring-[#C5221F] rounded-sm transition-colors duration-150";

export const Btn = ({ variant = "primary", className = "", ...props }) => {
  const styles = {
    primary: "bg-[#C5221F] hover:bg-[#A81B18] text-white",
    dark: "bg-[#0A1128] hover:bg-slate-800 text-white",
    ghost: "border border-slate-300 text-slate-700 hover:border-slate-500 bg-white",
    danger: "border border-red-300 text-red-700 hover:bg-red-50 bg-white",
  };
  return (
    <button
      className={`inline-flex items-center justify-center gap-2 px-4 py-2.5 text-xs font-bold uppercase tracking-widest rounded-sm transition-colors duration-150 disabled:opacity-50 disabled:cursor-not-allowed ${styles[variant]} ${className}`}
      {...props}
    />
  );
};

export const ConfirmDialog = ({ open, title, message, confirmLabel = "Delete", onConfirm, onClose, busy }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center p-4" data-testid="confirm-dialog">
      <div className="absolute inset-0 bg-[#0A1128]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-sm shadow-2xl max-w-md w-full p-7">
        <h3 className="font-display text-lg font-bold text-slate-900">{title}</h3>
        <p className="mt-2.5 text-sm text-slate-600 leading-relaxed">{message}</p>
        <div className="mt-6 flex justify-end gap-3">
          <Btn variant="ghost" onClick={onClose} data-testid="confirm-cancel-btn">Cancel</Btn>
          <Btn variant="danger" onClick={onConfirm} disabled={busy} data-testid="confirm-action-btn">
            {busy && <Loader2 size={14} className="animate-spin" />} {confirmLabel}
          </Btn>
        </div>
      </div>
    </div>
  );
};

export const Modal = ({ open, onClose, title, children, wide }) => {
  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[70] flex items-start justify-center p-4 overflow-y-auto">
      <div className="absolute inset-0 bg-[#0A1128]/60 backdrop-blur-sm" onClick={onClose} />
      <div className={`relative bg-white rounded-sm shadow-2xl w-full ${wide ? "max-w-4xl" : "max-w-lg"} my-8`}>
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display text-base font-bold text-slate-900">{title}</h3>
          <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700" data-testid="modal-close-btn"><X size={18} /></button>
        </div>
        <div className="p-6">{children}</div>
      </div>
    </div>
  );
};

export const MediaPicker = ({ open, onClose, onSelect, multiple = false }) => {
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);
  const [selected, setSelected] = useState([]);
  const fileRef = useRef(null);

  const load = () => adminApi.get("/media").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { if (open) { setSelected([]); load(); } }, [open]);

  const upload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setBusy(true);
    try {
      const fd = new FormData();
      files.forEach((f) => fd.append("files", f));
      await adminApi.post("/media", fd);
      toast.success("Image uploaded");
      await load();
    } catch (err) {
      toast.error(fmtErr(err));
    } finally {
      setBusy(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  if (!open) return null;
  return (
    <div className="fixed inset-0 z-[75] flex items-start justify-center p-4 overflow-y-auto" data-testid="media-picker">
      <div className="absolute inset-0 bg-[#0A1128]/60 backdrop-blur-sm" onClick={onClose} />
      <div className="relative bg-white rounded-sm shadow-2xl w-full max-w-4xl my-8">
        <div className="flex items-center justify-between px-6 py-4 border-b border-slate-200">
          <h3 className="font-display text-base font-bold text-slate-900">Media Library</h3>
          <div className="flex items-center gap-3">
            <input ref={fileRef} type="file" accept=".jpg,.jpeg,.png,.webp" multiple className="hidden" onChange={upload} data-testid="media-picker-upload-input" />
            <Btn onClick={() => fileRef.current?.click()} disabled={busy} data-testid="media-picker-upload-btn">
              {busy ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />} Upload
            </Btn>
            <button onClick={onClose} className="p-1.5 text-slate-400 hover:text-slate-700"><X size={18} /></button>
          </div>
        </div>
        <div className="p-6">
          {items.length === 0 ? (
            <div className="py-14 text-center text-slate-400">
              <ImageOff size={32} className="mx-auto mb-3" />
              <p className="text-sm">No uploads yet. Upload JPG, PNG or WebP images (max 15 MB each).</p>
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 lg:grid-cols-5 gap-3 max-h-[50vh] overflow-y-auto">
              {items.map((m) => (
                <button
                  key={m.id}
                  onClick={() => {
                    if (multiple) {
                      setSelected((s) => (s.includes(m.url) ? s.filter((x) => x !== m.url) : [...s, m.url]));
                    } else {
                      onSelect(m.url);
                      onClose();
                    }
                  }}
                  data-testid={`media-pick-${m.id}`}
                  className={`relative aspect-square overflow-hidden border-2 rounded-sm ${selected.includes(m.url) ? "border-[#C5221F]" : "border-transparent hover:border-slate-300"}`}
                >
                  <img src={m.thumb_url} alt={m.alt || m.title} className="w-full h-full object-cover" loading="lazy" />
                </button>
              ))}
            </div>
          )}
          {multiple && (
            <div className="mt-5 flex items-center justify-between border-t border-slate-200 pt-4">
              <p className="text-xs text-slate-500">{selected.length} selected</p>
              <Btn onClick={() => { onSelect(selected); onClose(); }} disabled={!selected.length} data-testid="media-picker-confirm-btn">
                Add Selected
              </Btn>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export const Thumb = ({ src, alt = "" }) =>
  src ? (
    <img src={src} alt={alt} className="w-20 h-14 object-cover border border-slate-200 rounded-sm" />
  ) : (
    <span className="w-20 h-14 flex items-center justify-center bg-slate-100 border border-slate-200 rounded-sm text-slate-300"><ImageOff size={18} /></span>
  );

export const AdminAuthShell = ({ children }) => (
  <div
    className="min-h-screen flex items-center justify-center p-4 bg-cover bg-center bg-no-repeat relative"
    style={{ backgroundImage: "url(/assets/img/admin-login-bg.jpg)" }}
    data-testid="admin-auth-shell"
  >
    <div className="absolute inset-0 bg-[#0A1128]/70" />
    <div className="relative w-full max-w-sm">
      <div className="flex items-center gap-3 justify-center mb-8">
        <span className="bg-[#C5221F] text-white font-display font-extrabold w-11 h-11 flex items-center justify-center">G.R</span>
        <span className="leading-tight">
          <span className="block font-display font-extrabold text-white">G. R. ENGINEERING</span>
          <span className="block text-[10px] text-slate-300 uppercase tracking-[0.3em]">Admin Panel</span>
        </span>
      </div>
      {children}
    </div>
  </div>
);
